import { getIsBitSet, ListViewBitFlagsMasks } from 'owa-bit-flags/lib/utils/listViewBitFlagsUtil';
import { getStore } from '../store/Store';
import { isReadingPanePositionRight } from '../selectors/readingPanePosition';
import { onClientReadingPanePositionChange } from '../actions/onClientReadingPanePositionChange';
import { setShowFolderPane } from '../actions/setShowFolderPane';
import BrowserWidthBucket from '../store/schema/BrowserWidthBucket';
import LayoutChangeSource from '../store/schema/LayoutChangeSource';
import logFolderPaneState from '../helpers/logFolderPaneState';
import ReadingPanePosition from 'owa-session-store/lib/store/schema/ReadingPanePosition';
import setBrowserWidthBucket from '../mutators/setBrowserWidthBucket';
import setCurrentClientReadingPanePosition from '../mutators/setCurrentClientReadingPanePositionMutator';
import getAvailableWidthBucket from './getAvailableWidthBucket';

export function initializeDynamicLayout() {
    window.addEventListener('resize', onWindowResize);

    // Calculate once on boot.
    onAvailableWidthChange(LayoutChangeSource.Init);
}

/**
 * On Window resize handler.
 */
function onWindowResize() {
    onAvailableWidthChange(LayoutChangeSource.WindowResize);
}

/**
 * Update the layout on the available app width change
 * @param source source of the width change
 */
function onAvailableWidthChange(source: LayoutChangeSource) {
    // Update available app width bucket
    const availableWidthBucket = getAvailableWidthBucket(source != LayoutChangeSource.Init);

    // No-op if the width bucket did not change
    if (getStore().browserWidthBucket == availableWidthBucket) {
        return;
    }

    setBrowserWidthBucket(availableWidthBucket);

    // Make appropriate layout changes as per the available width bucket
    switch (availableWidthBucket) {
        case BrowserWidthBucket.From418_Below:
            collapseFolderPane(source);
            setClientReadingPanePositionOff();
            addOverflowOnBody();
            break;

        case BrowserWidthBucket.From419_To554:
        case BrowserWidthBucket.From555_To768:
            collapseFolderPane(source);
            setClientReadingPanePositionOff();
            hideOverflowOnBody();
            break;

        case BrowserWidthBucket.From769_To918:
            collapseFolderPane(source);
            resetClientReadingPanePosition();
            hideOverflowOnBody();
            break;

        case BrowserWidthBucket.From919_To999:
        case BrowserWidthBucket.From1000_To1199:
        case BrowserWidthBucket.From1200_To1399:
        case BrowserWidthBucket.From1400_To1599:
        case BrowserWidthBucket.From1600_Above:
        default:
            expandFolderPane(source);
            resetClientReadingPanePosition();
            hideOverflowOnBody();
            break;
    }
}

function addOverflowOnBody() {
    document.body.style.overflowX = 'auto';
}

function hideOverflowOnBody() {
    document.body.style.overflow = 'hidden';
}

/**
 * Force hide folder pane as determined by the current browser width bucket
 * @param source the layout change source
 */
function collapseFolderPane(source: LayoutChangeSource) {
    if (getStore().showFolderPane) {
        setShowFolderPane(false);
        logFolderPaneState(
            source,
            false /* folderPaneNewShowState */,
            true /* folderPaneOldShowState */
        );
    }
}

/**
 * Try expand folder pane as determined by the current browser width bucket
 * @param source the layout change source
 */
function expandFolderPane(source: LayoutChangeSource) {
    const showFolderPane = !getIsBitSet(ListViewBitFlagsMasks.FolderPaneCollapsed);

    if (showFolderPane && !getStore().showFolderPane) {
        setShowFolderPane(true);
        logFolderPaneState(
            source,
            true /* folderPaneNewShowState */,
            false /* folderPaneOldShowState */
        );
    }
}

/**
 * Sets the client reading pane position best suited for the current browser layout
 */
function setClientReadingPanePositionOff() {
    // Change RP position to Off if it is Right
    const currentReadingPanePositionRight = isReadingPanePositionRight();
    if (!currentReadingPanePositionRight) {
        return;
    }

    setCurrentClientReadingPanePosition(ReadingPanePosition.Off);
    onClientReadingPanePositionChange();
}

/**
 * Resets the client reading pane position
 */
function resetClientReadingPanePosition() {
    setCurrentClientReadingPanePosition(null);
    onClientReadingPanePositionChange();
}
